/**
 * Sekme geçişlerinde tekrar okumayı önlemek için GET yanıtlarını önbelleğe alır.
 * Yalnızca cari detay (kart / satış / para / işlem / resim vb.) için kullanın.
 * Liste / grid / rapor aramaları cache'lenmez — her zaman sunucuya gidin.
 */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 dakika
const cache = new Map();

let idbModulePromise = null;
function loadIdbModule() {
  if (!idbModulePromise) {
    idbModulePromise = import('./syncIndexedDb.js').catch(() => null);
  }
  return idbModulePromise;
}

function isExpired(entry) {
  return !entry || Date.now() - entry.ts > CACHE_TTL_MS;
}

export function getCached(key) {
  const entry = cache.get(key);
  if (isExpired(entry)) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

export function setCached(key, data) {
  cache.set(key, { data, ts: Date.now() });
  void loadIdbModule().then((mod) => mod?.idbSetHttpCache?.(key, data, Date.now())).catch(() => { /* ignore */ });
}

export function clearCached(key) {
  cache.delete(key);
  void loadIdbModule().then((mod) => mod?.idbDeleteHttpCache?.(key)).catch(() => { /* ignore */ });
}

const inFlightGeneric = new Map();

/**
 * Genel amaçlı cache + in-flight dedupe (cari detay panelleri).
 * Liste / rapor endpoint'lerinde kullanmayın.
 *
 * @param {string} cacheKey
 * @param {() => Promise<any>} fetchFn - Doğrudan veri döndürmeli (Axios response değil)
 * @returns {Promise<any>}
 */
export function getCachedOrFetch(cacheKey, fetchFn) {
  const cached = getCached(cacheKey);
  if (cached !== null) return Promise.resolve(cached);
  if (inFlightGeneric.has(cacheKey)) {
    return inFlightGeneric.get(cacheKey);
  }
  const p = Promise.resolve()
    .then(async () => {
      const mod = await loadIdbModule();
      if (mod?.idbGetHttpCache) {
        const entry = await mod.idbGetHttpCache(cacheKey);
        if (entry && !isExpired(entry)) {
          setCached(cacheKey, entry.data);
          return entry.data;
        }
      }
      return fetchFn();
    })
    .then((data) => {
      const finalData = data === undefined ? null : data;
      if (finalData !== null) {
        setCached(cacheKey, finalData);
      }
      inFlightGeneric.delete(cacheKey);
      return finalData;
    })
    .catch((err) => {
      inFlightGeneric.delete(cacheKey);
      throw err;
    });
  inFlightGeneric.set(cacheKey, p);
  return p;
}

/** CARI_KART/rec için cache key */
export function cariRecCacheKey(carRef, cid, ap) {
  return `CARI_REC_${carRef}_${cid}_${ap}`;
}

/** Tek CAR_REF'li endpoint'ler için (para-ozet, islemlist_by_car, lst?CAR_REF, carResList, getSablonCariList, benzercari) */
export function carRefCacheKey(prefix, carRef, suffix = '') {
  return `${prefix}_${carRef}${suffix ? '_' + suffix : ''}`;
}

/** CAS_REF bazlı endpoint'ler için (islemlist, paraList, islemList, CARI_EKIMKANALALIM/list) – satış satırı değişiminde tekrar fetch önlenir */
export function casRefCacheKey(prefix, casRef) {
  return `${prefix}_CAS_${casRef}`;
}
